/* eslint-disable camelcase */
import React, { createContext, useEffect, useState } from 'react'
import api from '../services/api'
import { useSocket } from './socketProvider'
import { User } from '@phosphor-icons/react'

interface EmployessDataProps {
  id: string
  login: string
  password: string
  role: string
  Employee: {
    id_base: string
    type: string
  }
  UserInfo: {
    id_base: string
    name: string
    email: string
    phone: string
    birthdate: string
  }
  base: string
  outpost: string
  Address: {
    address: string
    zipcode: string
    burgh: string
    city: string
    state: string
    latitude: number
    longitude: number
  }
}

interface BaseDataProps {
  id: string
  base: string
  outpost: string
  address: {
    address: string
    zipcode: string
    burgh: string
    city: string
    state: string
    latitude: number
    longitude: number
  }
}

interface EmployessProps {
  employees?: EmployessDataProps[]
  UserInfo?: EmployessDataProps[]
  baseList?: BaseDataProps[] | null
  setBaseList?: React.Dispatch<React.SetStateAction<BaseDataProps[] | null>>
  formData?: EmployessDataProps
  setFormData?: React.Dispatch<React.SetStateAction<EmployessDataProps | null>>
  baseName?: string
  setBaseName?: React.Dispatch<React.SetStateAction<string>>
  setEmployees?: React.Dispatch<React.SetStateAction<string>>
  baseOptions?: { id: string; value: string }[] | null
  outpostOption?: { value: string }[] | null
  findBaseName?: ((id: string) => void) | undefined
  handleCreateEmployee?: (event: React.FormEvent) => void
  handleUpdateEmployee?: (event: React.FormEvent) => void
  handleGetTable?: (active: boolean) => void
  maskPhone: (value: string) => string
  isFormDataValid?: (
    confirm: React.Dispatch<React.SetStateAction<boolean>>,
  ) => void
  errorMessage: string
  setErrorModal: () => void
  errorModal: boolean
  setRegistered: () => void
  registered: boolean
}

export const Context = createContext<EmployessProps>()

const ContextProvider = (props) => {
  const [employees, setEmployees] = useState<EmployessDataProps[]>([])

  const [baseList, setBaseList] = useState<BaseDataProps[] | null>(null)

  const [formData, setFormData] = useState<EmployessDataProps>({
    id: '',
    login: '',
    password: '',
    role: '',
    Employee: { id_base: '', type: '' },
    UserInfo: {
      id_base: '',
      name: '',
      email: '',
      phone: '',
      birthdate: '',
    },
    base: '',
    outpost: '',
    Address: {
      address: '',
      zipcode: '',
      burgh: '',
      city: '',
      state: '',
      latitude: 0,
      longitude: 0,
    },
  })

  const [baseName, setBaseName] = useState<string>('')

  const [errorModal, setErrorModal] = useState<boolean>(false)
  const [registered, setRegistered] = useState<boolean>(false)

  const [errorMessage, setErrorMessage] = useState<string>('')

  const { isConnected } = useSocket()

  // fetch employees table
  useEffect(() => {
    api
      .get('/v1/dashboard/employees/table', {
        params: { active: true }, // ou active: false dependendo do que você quer buscar
      })
      .then((res) => {
        console.log('EMPLOYESS:', res.data)
        setEmployees(res.data)
      })
      .catch((err) => {
        console.log(err)
      })
  }, [isConnected])

  useEffect(() => {
    api
      .get('/v1/dashboard/base/list')
      .then((res) => {
        setBaseList(res.data)
      })
      .catch((err) => {
        console.log(err)
      })
  }, [isConnected])

  const baseOptions = baseList?.map((item) => {
    const id = item.id
    const value = item.base

    return { id, value }
  })

  const outpostOption = baseList?.map((item) => {
    const value = item.outpost

    return { value }
  })

  const findBaseName = (id) => {
    const baseOption = baseOptions?.find((item) => {
      return item.id === id
    })
    if (baseOption) {
      setBaseName(baseOption.id)
    }
  }

  const handleGetTable = async (active: boolean) => {
    try {
      const res = await api.get('/v1/dashboard/employees/table', {
        params: { active }, // ou active: false, dependendo do que você quer buscar
      })

      console.log('EMPLOYEES:', res.data)
      setEmployees(res.data)
    } catch (err) {
      console.error('Erro ao buscar funcionários:', err)
    }
  }

  const handleCreateEmployee = async (event: React.FormEvent) => {
    try {
      event.preventDefault()

      const { login, password, Employee, UserInfo, role, base, outpost } =
        formData
      const data = {
        login,
        password,
        name: UserInfo.name,
        email: UserInfo.email,
        phone: UserInfo.phone,
        birthdate: UserInfo.birthdate,
        role,
        type: Employee.type,
        base: Employee.id_base,
        outpost,
      }
      await api.post('/v1/dashboard/employees/create', data).then(() => {
        setRegistered(true)
      })
    } catch (err) {
      const errors = {
        'User with this login already exists':
          'O login escolhido já está em uso. Por favor, escolha um login diferente.',
        'User with this email already exists':
          'O email escolhido já está em uso. Por favor, escolha um email diferente.',
        'User with this phone already exists':
          'O telefone escolhido já está em uso. Por favor, escolha um telefone diferente.',
      }

      if (errors[err.response.data]) {
        setErrorModal(true)
        setErrorMessage(errors[err.response.data])
      }
    }
  }

  const handleUpdateEmployee = async (event: React.FormEvent) => {
    try {
      event.preventDefault()

      const {
        login,
        Address,
        Employee,
        UserInfo,
        password,
        role,
        base,
        outpost,
      } = formData

      const newFormData = new FormData()

      newFormData.append('login', login)
      newFormData.append('password', password)
      newFormData.append('role', role)
      newFormData.append('base', base)
      newFormData.append('outpost', outpost)

      // Appending UserInfo
      newFormData.append(
        'UserInfo',
        JSON.stringify({
          id_base: Employee.id_base,
          name: UserInfo.name,
          email: UserInfo.email,
          phone: UserInfo.phone,
          birthdate: UserInfo.birthdate || '',
        }),
      )

      // Appending Employee
      newFormData.append(
        'Employee',
        JSON.stringify({
          id_base: Employee.id_base,
          type: Employee.type,
        }),
      )

      // Appending Address
      newFormData.append(
        'Address',
        JSON.stringify({
          address: Address.address,
          zipcode: Address.zipcode,
          burgh: Address.burgh,
          city: Address.city,
          state: Address.state,
          latitude: Address.latitude,
          longitude: Address.longitude,
        }),
      )

      // Aguarde a conclusão da solicitação antes de prosseguir
      await api.post('/v1/dashboard/employees/update', newFormData)
      console.log('Dados enviados com sucesso!', formData, newFormData)
    } catch (err) {
      console.error('Erro ao enviar dados:', err)
    }
  }

  const isFormDataValid = (
    confirm: React.Dispatch<React.SetStateAction<boolean>>,
  ): void => {
    const { login, Employee, UserInfo, password, role, outpost } = formData

    // Verificar se algum campo obrigatório está vazio
    if (
      !login ||
      !UserInfo ||
      !Employee ||
      !Employee.id_base ||
      !Employee.type ||
      !outpost ||
      !password ||
      !role
    ) {
      console.log('Algum campo obrigatório está vazio', {
        login,
        Employee,
        UserInfo,
        password,
        role,
        outpost,
      })
      return
    }

    console.log('Todos os campos obrigatórios estão preenchidos')
    confirm((prev) => !prev)
  }

  const maskPhone = (value) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{4})(\d+?)$/, '$1')
  }

  return (
    <Context.Provider
      value={{
        // addEmployee,
        // updatedEmployee,
        employees,
        baseList,
        setBaseList,
        formData,
        setFormData,
        setEmployees,
        baseName,
        setBaseName,
        baseOptions,
        outpostOption,
        findBaseName,
        handleCreateEmployee,
        handleUpdateEmployee,
        isFormDataValid,
        maskPhone,
        errorMessage,
        errorModal,
        setErrorModal,
        setRegistered,
        registered,
        handleGetTable,
      }}
    >
      {props.children}
    </Context.Provider>
  )
}

export default ContextProvider
